import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";

const RECOVERY_KEY = "portal:callback-recovery-attempted";

export default function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [retryError, setRetryError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [recoveryBlocked, setRecoveryBlocked] = useState(false);
  const attemptedRecovery = useRef(false);

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      const saved = sessionStorage.getItem("post_login_path");
      const dest = saved && saved.startsWith("/") && !saved.startsWith("//") && !saved.startsWith("/auth/")
        ? saved : "/reporttool";
      sessionStorage.removeItem("post_login_path");
      sessionStorage.removeItem(RECOVERY_KEY);
      navigate(dest, { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  const retry = useCallback(async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      // Persist across the redirect so a second failed callback cannot loop.
      sessionStorage.setItem(RECOVERY_KEY, "1");
      // Start a fresh OIDC request instead of reusing a consumed history entry.
      await auth.signinRedirect({ redirectMethod: "replace" });
    } catch (error) {
      setRetryError(error);
      setRetrying(false);
    }
  }, [auth.signinRedirect]);

  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || auth.activeNavigator || attemptedRecovery.current) return;
    attemptedRecovery.current = true;
    // An explicit denial should remain under the user's control.
    if (auth.error?.error === "access_denied") {
      setRecoveryBlocked(true);
      return;
    }
    try {
      if (sessionStorage.getItem(RECOVERY_KEY)) {
        setRecoveryBlocked(true);
        return;
      }
    } catch {
      setRecoveryBlocked(true);
      return; // Without persistent state, automatic retry could loop.
    }
    retry();
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, retry]);

  if (auth.isLoading || auth.isAuthenticated || auth.activeNavigator || retrying || (!recoveryBlocked && !retryError)) {
    return null;
  }

  return (
    <div style={{ padding: 24 }}>
      <p role="alert">We couldn’t complete sign-in. Please try again to open the portal.</p>
      {retryError && <p role="alert">Sign-in could not be started. Please try again.</p>}
      <button onClick={retry} disabled={retrying || Boolean(auth.activeNavigator)}>
        {retrying ? "Signing in…" : "Continue to portal"}
      </button>
    </div>
  );
}
